import { FareCode } from "@silversea-ssc/web-fares";
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import { CancellationPolicyModalContentFragment } from "types/graphql-types";
import { PaymentTerms } from "../helpers";

const FarePenaltiesModal = React.lazy(() => import("./FarePenaltiesModal"));

type FarePenaltiesModalContextValue = {
  content: CancellationPolicyModalContentFragment;
  remotePaymentTerms: (PaymentTerms | undefined)[];
  remotePaymentTermByFareCode: (fareCode: FareCode) => PaymentTerms | undefined;
  onRequestOpenPenaltiesModal: (args: PenaltiesModalInfo) => void;
};

const FarePenaltiesModalContext =
  React.createContext<FarePenaltiesModalContextValue>({
    content: {},
    remotePaymentTerms: [],
    onRequestOpenPenaltiesModal: () => {
      ///
    },
    remotePaymentTermByFareCode: (_fareCode: FareCode) => undefined
  });

type PenaltiesModalInfo = {
  fareCode: FareCode;
};

type Props = {
  content: CancellationPolicyModalContentFragment;
  showCruiseGroupDisclaimer: boolean; // NOTE: on cruise page set to false
  remotePaymentTerms: (PaymentTerms | undefined)[];
};

export const FarePenaltiesModalProvider: React.FC<PropsWithChildren<Props>> = ({
  children,
  content,
  showCruiseGroupDisclaimer,
  remotePaymentTerms
}) => {
  const [cancellationPolicyInfo, setCancellationPolicyInfo] = useState<
    PenaltiesModalInfo | undefined
  >();

  const onRequestOpenPenaltiesModal = useCallback(
    (args: PenaltiesModalInfo) => {
      setCancellationPolicyInfo(args);
    },
    []
  );

  const contextValue = useMemo(() => {
    return {
      content,
      remotePaymentTerms,
      onRequestOpenPenaltiesModal,
      remotePaymentTermByFareCode:
        remotePaymentTermByFareCode(remotePaymentTerms)
    };
  }, [content, remotePaymentTerms, onRequestOpenPenaltiesModal]);

  return (
    <FarePenaltiesModalContext.Provider value={contextValue}>
      {children}
      {cancellationPolicyInfo && (
        <React.Suspense>
          <FarePenaltiesModal
            content={content}
            modal={{
              isOpen: true,
              onRequestClose: () => setCancellationPolicyInfo(undefined)
            }}
            size="big"
            remotePaymentTerms={remotePaymentTerms}
            showCruiseGroupDisclaimer={showCruiseGroupDisclaimer}
            selectedFareCode={cancellationPolicyInfo.fareCode}
          />
        </React.Suspense>
      )}
    </FarePenaltiesModalContext.Provider>
  );
};

const remotePaymentTermByFareCode =
  (remotePaymentTerms: (PaymentTerms | undefined)[]) =>
  (fareCode: FareCode) => {
    return remotePaymentTerms.find((r) => r?.fareCode === fareCode);
  };

export const useFarePenaltiesModal = () => {
  return useContext(FarePenaltiesModalContext);
};
